import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { MOCK, isSchemaPending } from '../lib/supabase.js';
import DbPending from '../components/DbPending.jsx';
import { SHIPMENTS as MOCK_SHIPMENTS, shipName as mockShipName } from '../lib/mockData.js';
import * as db from '../lib/data.js';
import { toRailVM, toBoardVM, sortRail, coGate } from '../lib/viewmodel.js';
import { daysUntil } from '../lib/urgency.js';
import { guessReqType, reqLabel, reqHelp } from '../lib/reqtypes.js';
import { useAuth } from '../lib/auth.jsx';
import { MaterialGapButton, useMaterialsReady } from '../components/MaterialEnqueueModal.jsx';
import { Modal, CreateShipmentModal, AddConsignmentModal, VerifyCOModal, EditShipmentModal, EditConsignmentModal, SwitchModeModal, COMMON_COUNTRIES } from '../components/Modals.jsx';
import { NudgeModal } from '../components/NudgeModal.jsx';
import Journal, { EventLog } from '../components/Journal.jsx';
import { draftNudge } from '../lib/nudge.js';

// Import Console — บอร์ดจับคู่เอกสาร (LIVE = document_requirement LEFT JOIN document)
// MOCK mode (ไม่มี env) ยังโชว์ข้อมูลจำลองจาก mockups/console.html

const STATUS = {
  ok: { mk: '✓', foot: 'ครบ' },
  f:  { mk: '!', foot: '' },
  m:  { mk: '○', foot: '' },
  na: { mk: '—', foot: 'ไม่ต้องใช้' },
};

const ModeIcon = ({ mode }) => mode === 'air' ? (
  <svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15l-8-3V5.5a1.5 1.5 0 0 0-3 0V12l-8 3v2l8-2v3.5L6 21v1l6-1.5L18 22v-1l-2-2.5V15z"/>
  </svg>
) : (
  <svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 17c1.5 0 1.5 1 3 1s1.5-1 3-1 1.5 1 3 1 1.5-1 3-1 1.5 1 3 1 1.5-1 3-1"/>
    <path d="M5 17l1.5-5h11L19 17"/><path d="M12 7v5M9 9h6"/>
  </svg>
);
// ── ค้นหาตู้: รวมทุกอย่างที่ "เกี่ยวข้องกับตู้นั้น" เป็นข้อความก้อนเดียวแล้วค้นทีเดียว ──
//   รหัสตู้ · ชื่อที่ตั้ง · ชื่อ supplier · เลข invoice · house B/L · master B/L · forwarder · ท่า ·
//   และ "ชื่อไฟล์เอกสารทุกใบ" (เลข invoice ของ forwarder มักอยู่บนชื่อไฟล์ ไม่ได้อยู่ในช่องไหนเลย)
const norm = (v) => (v ?? '').toString().normalize('NFC').toLowerCase();
function shipHaystack(vm, docsByShip) {
  const r = vm.raw ?? {};
  const cons = vm.consignments ?? [];
  return [
    vm.id, vm.label, vm.displayName, r.code, r.label, r.master_bl, r.forwarder,
    r.origin_port, r.dest_port,
    // ⚠ อ่าน mode จาก vm ไม่ใช่ raw — โหมดตัวอย่างไม่มี vm.raw (เคยทำให้ค้น "air" ไม่เจอ)
    vm.mode, vm.mode === 'air' ? 'ทางอากาศ เครื่องบิน air' : 'ทางเรือ เรือ sea',
    vm.phase, vm.route, vm.eta, r.status, vm.done ? 'ปิดงานแล้ว เสร็จแล้ว' : 'กำลังทำ',
    ...cons.flatMap(c => [c.supplier_name, c.invoice_no, c.house_bl, c.origin_country]),
    ...(docsByShip[vm.dbId] ?? []),
  ].filter(Boolean).map(norm).join(' \u0001 ');
}
// พิมพ์หลายคำ = ต้องเจอทุกคำ (AND) — ค้น "snow peak นิ่ง" ได้ผลแคบลงเรื่อย ๆ
const matchShip = (hay, terms) => terms.every(t => hay.includes(t));

const DocIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5M9 13h6M9 17h6"/>
  </svg>
);
const FileIcon = () => (
  <svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M13 3v5h5"/>
  </svg>
);
const ClockIcon = () => (
  <svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>
  </svg>
);
const StampIcon = () => (
  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l5 5L20 6"/></svg>
);
const Stamp = ({ sm }) => (
  <span className={`stamp ${sm ? 'sm' : ''}`} title="ปิดงานแล้ว — รับของเข้าคลังครบ">
    <StampIcon/>ปิดงานแล้ว
  </span>
);

function Ring({ pct }) {
  const r = 17, c = 2 * Math.PI * r;
  const [off, setOff] = useState(c);
  useEffect(() => {
    const t = requestAnimationFrame(() => setOff(c * (1 - pct / 100)));
    return () => cancelAnimationFrame(t);
  }, [pct]);
  return (
    <svg width="46" height="46" viewBox="0 0 46 46" style={{ flex: '0 0 auto' }}>
      <circle cx="23" cy="23" r={r} fill="none" stroke="rgba(24,26,38,.10)" strokeWidth="4.5"/>
      <circle cx="23" cy="23" r={r} fill="none" strokeWidth="4.5" strokeLinecap="round"
        strokeDasharray={c.toFixed(1)} strokeDashoffset={off.toFixed(1)}
        transform="rotate(-90 23 23)"
        style={{ stroke: 'var(--u1)', transition: 'stroke-dashoffset 1.05s cubic-bezier(.2,.8,.2,1)' }}/>
      <text x="23" y="23" textAnchor="middle" dominantBaseline="central"
        fontFamily="var(--mono)" fontSize="12" fontWeight="700" fill="var(--ink)">{pct}</text>
    </svg>
  );
}

function Bar({ pct }) {
  const [w, setW] = useState(0);
  useEffect(() => {
    const t = requestAnimationFrame(() => setW(pct));
    return () => cancelAnimationFrame(t);
  }, [pct]);
  return <div className="bar"><i style={{ width: `${w}%` }}/></div>;
}

const SideBadge = ({ d }) => d.side
  ? <span className={`sbadge ${d.side}`}>{d.side === 'exp' ? 'ต้นทางส่ง' : 'ขอในไทย'}</span>
  : null;

// ใบอนุญาตที่รอบนี้ไม่ต้องใช้ — ต้องบอกที่มาเสมอ คนถัดไปจะได้เช็คต่อได้ (ระบบไม่สรุปเองว่าไม่ต้องขอ)
function PermitWaiveModal({ d, onClose, onSave }) {
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const save = async () => {
    setBusy(true); setErr(null);
    try { await onSave(why.trim()); onClose(); }
    catch (e) { setErr(e.message ?? String(e)); setBusy(false); }
  };
  return (
    <Modal title={`รอบนี้ไม่ใช้ ${d.t}${d.side === 'exp' ? ' (ต้นทางส่ง)' : d.side === 'imp' ? ' (ขอในไทย)' : ''}?`} sub="การ์ดนี้จะไม่นับว่าขาด และกดกลับมาใช้ได้ภายหลัง" onClose={onClose}>
      <div className="fld">
        <label className="flabel" htmlFor="pw-why">รู้ได้อย่างไรว่ารอบนี้ไม่ต้องใช้?</label>
        <textarea id="pw-why" rows={3} value={why} onChange={e => setWhy(e.target.value)}
          placeholder="เช่น ชิปปิ้งแจ้งเมื่อวันที่ … ว่าไม่ต้องยื่น หรือ หนังสือไม่เข้าข่ายเลขที่ …"/>
        <span className="fhint">ข้อความนี้จะอยู่ใน "ประวัติการทำงาน" ของตู้นี้</span>
      </div>
      {err && <div className="ferr">{err}</div>}
      <div className="mfoot">
        <button className="btn2" onClick={onClose}>ยกเลิก</button>
        <button className="btn1" disabled={busy || !why.trim()} onClick={save}>{busy ? 'กำลังบันทึก…' : 'บันทึกว่ารอบนี้ไม่ใช้'}</button>
      </div>
    </Modal>
  );
}

function DocCard({ d, onFill, onOpen, onNudge, onRemove, onDropFile, canNudge, onWaivePermit, onRestorePermit }) {
  const m = STATUS[d.s], k = `k-${d.k || 'customs'}`;
  const help = reqHelp(d.req?.req_type);
  const HelpTip = help ? <span className="cardhelp" title={help}>?</span> : null;
  const [dragOver, setDragOver] = useState(false);
  const canDrop = !!d.reqId && !!onDropFile;   // การ์ดจริง (มี req) รับไฟล์วางได้
  const dragProps = canDrop ? {
    onDragOver: (e) => { e.preventDefault(); if (!dragOver) setDragOver(true); },
    onDragLeave: () => setDragOver(false),
    onDrop: (e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) onDropFile(d, f); },
  } : {};

  if (d.s === 'm') return (
    <div className={`card m ${k} ${d.urgent ? 'urgent' : ''} ${dragOver ? 'dragover' : ''} tap`} title={help || undefined} {...dragProps}>
      <div className="ct"><span className="ctype">{d.t}<SideBadge d={d}/></span>{HelpTip}</div>
      <div className="cbody">{dragOver ? '⬇ วางไฟล์ที่นี่' : (d.note || 'ยังไม่มีเอกสาร — ลากไฟล์มาวาง หรือกดเติม')}</div>
      <div className="chase-row">
        <button className="chase" onClick={() => onFill?.(d)}>＋ เติมไฟล์</button>
        {canNudge && <button className="chase alt" onClick={() => onNudge?.(d)}>ตาม</button>}
        {/* ใบอนุญาตมีช่องบังคับ แต่บางรอบชิปปิ้งแจ้งว่าไม่ต้องยื่น — ไม่มีปุ่มนี้การ์ดจะค้างแดงตลอด */}
        {d.k === 'permit' && d.reqId && onWaivePermit && <button className="chase alt" onClick={() => onWaivePermit(d)}>รอบนี้ไม่ใช้</button>}
      </div>
    </div>
  );
  if (d.s === 'na') return (
    <div className={`card na ${k}`} title={help || undefined}>
      <div className="ct"><span className="ctype">{d.t}<SideBadge d={d}/></span><span className="mk">{m.mk}</span></div>
      <div className="cbody">{d.note || ''}</div>
      {d.k === 'permit' && d.reqId && onRestorePermit && (
        <div className="card-actions"><button className="cardbtn" onClick={() => onRestorePermit(d)}>กลับมาใช้</button></div>
      )}
      {/* ไม่ใช้สิทธิ์ FTA แต่เคยจับคู่ไฟล์ Form ไว้ — ยังต้องเปิดดูได้ (ช่องนี้ไม่ขึ้นในถาดเพราะยังผูกอยู่ · รีวิว 29 ต.ค.) */}
      {d.filePath
        ? <div className="cfoot fname" style={{ cursor: 'pointer' }} onClick={() => onOpen?.(d)} title="คลิกเพื่อเปิดดูไฟล์">{d.f || 'เปิดไฟล์'}</div>
        : d.k !== 'permit' && <div className="cfoot">{m.foot}</div>}
    </div>
  );
  const openable = !!d.filePath;
  return (
    <div className={`card ${d.s} ${k} ${openable ? 'openable' : ''} ${dragOver ? 'dragover' : ''}`}
      title={openable ? `${help ? help + ' · ' : ''}คลิกเพื่อเปิดดูไฟล์` : (help || undefined)} {...dragProps}>
      <div className="ct" onClick={() => openable && onOpen?.(d)} style={{ cursor: openable ? 'pointer' : 'default' }}>
        <span className="ctype">{d.t}<SideBadge d={d}/></span><span className="mk">{m.mk}</span>
      </div>
      <div className="cbody" onClick={() => openable && onOpen?.(d)} style={{ cursor: openable ? 'pointer' : 'default' }}>
        <DocIcon className="di"/><span className="fname">{d.f || ''}</span>
      </div>
      {d.reqId ? (
        <div className="card-actions">
          <button className="cardbtn" onClick={() => onFill?.(d)} title="อัพไฟล์ใหม่แทน">เปลี่ยน</button>
          <button className="cardbtn del" onClick={() => onRemove?.(d)} title="เอาไฟล์ออก กลับเป็นช่องว่าง">เอาออก</button>
          {dragOver && <span className="dropnote">⬇ วางเพื่อแทน</span>}
        </div>
      ) : <div className="cfoot">{d.s === 'f' ? d.note : m.foot}</div>}
    </div>
  );
}

function DocGroup({ g, actions, notice, onFill, onOpen, onNudge, onRemove, onDropFile, canNudge, onWaivePermit, onRestorePermit }) {
  const req = g.docs.filter(d => d.s !== 'na');
  const mm = req.filter(d => d.s === 'ok').length;
  const flag = g.docs.some(d => d.s === 'f');
  return (
    <div className="grp">
      <div className="grp-h">
        <span className="kind" style={{ background: g.hue }}>{g.kind}</span>
        <span className="nm" style={{ color: g.hue }}>{g.nm}</span>
        <span className="meta" title={g.origin ? 'ประเทศที่ supplier ส่งของมา (ไม่ใช่ประเทศที่ผลิตของแต่ละชิ้น)' : undefined}>
          {g.origin ? `ส่งจาก ${g.origin} · ${g.pos}` : g.meta}</span>
        {flag && <span className="flag">! ต้องตรวจ</span>}
        {actions}
        <span className="mm">จับคู่ {mm}/{req.length}</span>
      </div>
      {notice}
      <div className="cards">{g.docs.map((d, i) =>
        <DocCard key={d.reqId ?? i} d={d} onFill={onFill} onOpen={onOpen} onNudge={onNudge}
          onRemove={onRemove} onDropFile={onDropFile} canNudge={canNudge} onWaivePermit={onWaivePermit} onRestorePermit={onRestorePermit}/>)}</div>
    </div>
  );
}

function RailCard({ s, sel, onSelect }) {
  return (
    <button className={`shipcard u-${s.urgency} ${sel ? 'sel' : ''}`} onClick={onSelect}>
      <div className="cap"/>
      <div className="bd">
        <div className="ship-r1">
          <span className="ship-id">{s.id}</span>
          {/* ต่างกันที่ "ทรง + ไอคอน + คำ" ไม่ใช่แค่สี — อากาศ = เหลี่ยม · เรือ = มน */}
          <span className={`ship-mode ${s.mode}`} title={s.mode === 'air' ? 'ทางอากาศ' : 'ทางเรือ'}>
            <ModeIcon mode={s.mode}/>{s.mode === 'air' ? 'AIR' : 'SEA'}</span>
        </div>
        <div className="ship-brand">{s.displayName}</div>
        <div className="ship-r2"><span className="ship-route">{s.route}</span><Ring pct={s.pct}/></div>
        <div className="ship-r3">
          {s.done ? <Stamp sm/> : <><span className="dot"/><span className="eta-pill">{s.eta}</span></>}
        </div>
        <div className="ship-why">{s.why}</div>
      </div>
    </button>
  );
}

// mock → ทรงเดียวกับ live VM (เฉพาะ field ที่ UI ใช้)
const mockVMs = () => MOCK_SHIPMENTS.map(s => ({ ...s, displayName: mockShipName(s), dbId: s.id }));
const mockBoard = (s) => ({
  groups: s.consignments, shipmentDocs: s.shipmentDocs,
  tray: s.tray ? [{ id: 't1', file_name: s.tray.name, suggestion: s.tray.to }] : [],
});

export default function Console({ onOpenDossier, onStats, focusShip, onFocusConsumed, onSelect, onOpenMaterials }) {
  const { me, mock, session } = useAuth();
  const canAi = mock || ['admin', 'coordinator'].includes(me?.role);   // หาวัสดุด้วย AI — ไม่ใช่ can_see_cost (นั่นรวมบัญชีด้วย)
  const aiOn = useMaterialsReady(canAi);
  const actor = session?.user ? { id: session.user.id, role: me?.role ?? null } : null;

  const [ships, setShips] = useState(mock ? sortRail(mockVMs()) : []);
  const [currentId, setCurrentId] = useState(null);
  const [railView, setRailView] = useState('active'); // active | done
  // ไฟล์/ใบ CO ผูกกับตู้ที่โหลดมา ({ shipId, list }) — โชว์เฉพาะเมื่อตรงกับตู้ที่เปิดอยู่ (คำนวณใต้ current)
  //   🐛 เดิมเป็น list เปล่า ๆ → คำตอบของตู้ก่อนที่มาช้า (สลับตู้เร็ว / อัปไฟล์แล้วกดตู้อื่น) โชว์ใต้ตู้ใหม่
  //   แล้วปุ่มจับคู่/ลบในถาดทำกับไฟล์ของอีกตู้ — รีวิว 28 ต.ค. 69
  const [docState, setDocState] = useState({ shipId: null, list: [] });
  const [q, setQ] = useState('');                    // ค้นหาตู้
  const [docNames, setDocNames] = useState({});      // {shipment_id: [ชื่อไฟล์…]} ใช้ค้นหาอย่างเดียว
  const [certState, setCertState] = useState({ shipId: null, list: [] });
  // ของแต่ละ supplier ผลิตที่ประเทศไหน ({ shipId, map: {consignment_id: {CN: 95, …}} }) — แถบเตือนสิทธิ์ FTA (FtaStrip)
  const [originState, setOriginState] = useState({ shipId: null, map: {} });
  const [loading, setLoading] = useState(!mock);
  const [error, setError] = useState(null);          // โหลดพัง = เต็มหน้า
  const [actMsg, setActMsg] = useState(null);        // ทำ action พลาด (อัพ/ลบ/ปิดงาน) = แถบเตือน ไม่ทำลายทั้งหน้า
  const [waiveDoc, setWaiveDoc] = useState(null);    // การ์ดใบอนุญาตที่กำลังกด "รอบนี้ไม่ใช้"
  const [modal, setModal] = useState(null);          // 'create' | 'consign' | {verify} | {nudge}
  const fillRef = useRef(null);                       // requirement ที่กำลังจะเติมไฟล์
  const fileInput = useRef(null);
  const trayInput = useRef(null);
  const trayReqType = useRef(null);   // ชนิดเอกสารที่จะแนบ (Freight Invoice / อื่นๆ)

  const doneShips = ships.filter(x => db.isDone(x.raw?.status));
  const activeShips = ships.filter(x => !db.isDone(x.raw?.status));
  // ค้นหา = ข้ามแท็บ "กำลังทำ/เสร็จแล้ว" ไปเลย (หาตู้เก่าต้องเจอโดยไม่ต้องรู้ว่ามันปิดไปแล้วหรือยัง)
  const terms = useMemo(() => norm(q).split(/\s+/).filter(Boolean), [q]);
  const hits = useMemo(() => (
    terms.length ? ships.filter(x => matchShip(shipHaystack(x, docNames), terms)) : null
  ), [terms, ships, docNames]);
  const railShips = hits ?? (railView === 'done' ? doneShips : activeShips);
  const hitDone = hits ? hits.filter(x => db.isDone(x.raw?.status)).length : 0;
  // เลือกจากตู้ในแท็บที่เปิดอยู่ก่อน (rail กับ main ต้องตรงกัน) — เข้ามาใหม่ = โชว์งานที่กำลังทำ ไม่ใช่ตู้ปิดแล้ว
  const current = railShips.find(x => x.id === currentId) ?? railShips[0] ?? ships[0] ?? null;
  const documents = docState.shipId === current?.dbId ? docState.list : [];
  const certsReady = certState.shipId === current?.dbId;   // ยังไม่มา = ด่าน Form/CO ยังตัดสินไม่ได้ (ห้ามโชว์ว่า "ยังไม่ผ่าน")
  const certs = certsReady ? certState.list : [];
  const origins = originState.shipId === current?.dbId ? originState.map : {};

  // ส่งสรุปตัวเลขขึ้น header (ACTIVE / ต้องเร่ง / ถึงใน 7 วัน) ให้ตรงข้อมูลจริง
  useEffect(() => {
    onStats?.({
      active: activeShips.length,
      urgent: activeShips.filter(x => x.urgency === 'urgent').length,
      watch: activeShips.filter(x => x.urgency === 'watch').length,
    });
  }, [ships]);

  const [pending, setPending] = useState(false);
  const [supplierNames, setSupplierNames] = useState([]);   // จากแท็บ "แบรนด์" → datalist ตอนเพิ่ม/แก้ supplier
  const [partners, setPartners] = useState([]);             // สีประจำแบรนด์ (brand_hue) ที่ตั้งไว้ในแท็บ "แบรนด์"
  // ตู้ที่สั่งเปิด (จาก URL / แท็บ "วันนี้") ที่ยังไม่ได้เลือก — loadAll เลือกตู้นี้ตั้งแต่แรก
  //   ไม่งั้นเลือกตู้แรกของรายการคั่นกลาง แล้ว effect เอกสารของตู้นั้นวิ่งชนกับของตู้ที่สั่งเปิด (รีวิว 28 ต.ค. 69)
  const focusRef = useRef(focusShip);
  focusRef.current = focusShip;
  const loadAll = useCallback(async (keepId) => {   // คืนรายการตู้ที่โหลดได้ (พังคืน null) — refresh() ใช้หาตู้ล่าสุด
    if (mock) return null;
    let out = null;
    setLoading(true); setError(null); setPending(false);
    try {
      db.listPartners().then(ps => {
        setPartners(ps);
        setSupplierNames([...new Set(ps.flatMap(p => db.partnerNames(p)))].sort());
      }).catch(() => {});
      db.allDocNames().then(ds => {
        const m = {};
        for (const d of ds) (m[d.shipment_id] ??= []).push(d.file_name);
        setDocNames(m);
      }).catch(() => {});      // ค้นหายังใช้ได้ถ้าโหลดชื่อไฟล์ไม่ได้ แค่ค้นชื่อไฟล์ไม่เจอ
      const rows = await db.listShipments();
      const vms = sortRail(rows.map(toRailVM));
      setShips(vms);
      // default = ตู้แรกที่ "กำลังทำ" (ไม่ใช่ตู้ปิดแล้วที่ overdue เรียงมาก่อน)
      const firstActive = vms.find(x => !db.isDone(x.raw?.status));
      const want = keepId == null && focusRef.current ? vms.find(x => x.id === focusRef.current) : null;
      if (want) setRailView(db.isDone(want.raw?.status) ? 'done' : 'active');
      const id = keepId ?? want?.id ?? firstActive?.id ?? vms[0]?.id ?? null;
      setCurrentId(id);
      out = vms;
    } catch (e) {
      if (isSchemaPending(e)) setPending(true);
      else setError(e.message ?? String(e));
    }
    setLoading(false);
    return out;
  }, [mock]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // เปิดตู้เจาะจงจากแท็บ "วันนี้" / จาก URL ตอนรีเฟรช — สลับแท็บ rail (กำลังทำ/เสร็จแล้ว) ให้ตรงตู้แล้วเลือก
  useEffect(() => {
    if (!focusShip || !ships.length) return;
    const vm = ships.find(x => x.id === focusShip);
    if (vm) {
      setRailView(db.isDone(vm.raw?.status) ? 'done' : 'active');
      setCurrentId(vm.id);
    }
    onFocusConsumed?.(!!vm);   // false = ไม่มีตู้นี้แล้ว → App บอกคน (ไม่เปิดตู้อื่นแทนเงียบ ๆ)
  }, [focusShip, ships]);

  // ตู้ที่เปิดอยู่ → ใส่ใน URL (รีเฟรชแล้วกลับมาตู้เดิม) · รอให้ตู้ที่สั่งเปิด (focusShip) ถูกเลือกก่อน
  //   ไม่งั้น URL โดนเขียนเป็นตู้แรกของรายการคั่นกลาง
  useEffect(() => {
    if (focusShip || !current?.id) return;
    onSelect?.(current.id);
  }, [current?.id, focusShip]);

  const healed = useRef(new Set());
  // ตู้ที่ "โชว์อยู่" ตอนนี้ — งานที่ตอบกลับช้า (self-heal / refresh / ปิดงาน) ใช้ค่านี้ ไม่ใช่ค่าตอนเริ่ม
  //   ⚠ ต้องเป็น current.id ไม่ใช่ currentId: กดแท็บ "กำลังทำ/เสร็จแล้ว" ไม่เปลี่ยน currentId แต่ตู้ที่โชว์เปลี่ยน
  //   (รอบสองเคยใช้ currentId → ปิดงานแล้วจอกระโดดไปอีกตู้ที่มีปุ่มปิดงานเหมือนกัน กดซ้ำ = ปิดผิดตู้ — รีวิว 28 ต.ค. 69)
  const selRef = useRef(null);
  selRef.current = current?.id ?? currentId;
  const curDbRef = useRef(null); // dbId ของตู้ที่โชว์อยู่ตอนนี้ — refresh() ที่ตอบช้าห้ามเขียนไฟล์ของตู้เก่าลงตู้ใหม่
  curDbRef.current = current?.dbId ?? null;
  // เขียนไฟล์/ใบ CO ได้เฉพาะของตู้ที่เปิดอยู่ตอนนี้ — คำตอบของตู้อื่นที่มาช้าทิ้งไป
  const putDocs = (shipId, list) => { if (curDbRef.current === shipId) setDocState({ shipId, list }); };
  const putCerts = (shipId, list, failed = false) => { if (curDbRef.current === shipId) setCertState({ shipId, list, failed }); };
  const putOrigins = (shipId, map) => { if (curDbRef.current === shipId) setOriginState({ shipId, map }); };
  useEffect(() => {
    setActMsg(null);   // เปลี่ยนตู้ = ล้าง error เก่าของตู้ก่อน
    if (mock || !current) return;
    let alive = true;
    const shipId = current.dbId;
    (async () => {
      try {
        const [docs, cs, og] = await Promise.allSettled([
          db.getDocuments(shipId),
          db.getCertificates(current.consignments.map(c => c.id)),
          db.getConsignmentOrigins(current.consignments.map(c => c.id)),
        ]);
        if (!alive) return;
        if (docs.status === 'fulfilled') putDocs(shipId, docs.value);
        else setActMsg('โหลดรายการไฟล์ของตู้นี้ไม่สำเร็จ — ลองกดตู้นี้อีกครั้ง หรือรีเฟรชหน้า');
        if (og.status === 'fulfilled') putOrigins(shipId, og.value);   // พลาด = ไม่โชว์แถบเตือน FTA (ไม่ใช่ด่าน)
        // ใบ CO โหลดไม่ขึ้น → บอกตรง ๆ (เดิมเงียบแล้วด่านบอกว่า Form ยังไม่ผ่าน · ห้ามค้าง "กำลังโหลด…")
        if (cs.status === 'fulfilled') putCerts(shipId, cs.value); else putCerts(shipId, [], true);
        // self-heal: ตู้ที่สร้างก่อนแก้ (ไม่มี requirement เลย) → คำนวณช่องระดับตู้ให้
        if (!current.requirements.length && !healed.current.has(current.dbId)) {
          healed.current.add(current.dbId);
          await db.computeRequirements(current.dbId);
          // 🐛 เดิม loadAll(currentId) = ค่าตอนเริ่ม effect → ถ้าระหว่างรอมีการเลือกตู้อื่น (เปิดตู้จากแท็บ "วันนี้"
          //   / รีเฟรชที่หน้าตู้นั้น) โหลดเสร็จแล้วเด้งกลับตู้แรกของรายการ
          loadAll(selRef.current);
        }
      } catch { /* board แสดงเท่าที่มี */ }
    })();
    return () => { alive = false; };
  }, [mock, current?.dbId]);

  // โหลดตู้ใหม่ + โหลดรายการไฟล์ใหม่ (effect เอกสารผูกกับ current.dbId ซึ่งไม่เปลี่ยน → ต้องดึงเอง
  // ไม่งั้นอัปโหลด/ลบไฟล์แล้วชื่อไฟล์บนการ์ดไม่อัปเดตจนกว่าจะสลับตู้)
  const refresh = async () => {
    const id = current?.dbId;
    // ตู้ที่เลือก "ตอนนี้" (selRef) ไม่ใช่ตอนเริ่มกด — ระหว่างอัป/บันทึก คนอาจกดไปตู้อื่นแล้ว (เดิมเด้งกลับ)
    const vms = await loadAll(selRef.current);
    if (!mock && id) {
      // ใบ CO ด้วย — เดิมโหลดแค่ไฟล์ → กด "ตรวจ Form" แล้วปุ่ม/ป้าย "ยังไม่ยืนยัน Form" ค้างจนกว่าจะสลับตู้
      //   (ใช้ consignment จากรายการที่เพิ่งโหลด ไม่ใช่ของตอนเริ่มกด)
      const vm = vms?.find(x => x.dbId === id);
      const [d, c, og] = await Promise.allSettled([
        db.getDocuments(id),
        vm ? db.getCertificates(vm.consignments.map(x => x.id)) : Promise.resolve(null),
        vm ? db.getConsignmentOrigins(vm.consignments.map(x => x.id)) : Promise.resolve(null),
      ]);
      if (d.status === 'fulfilled') putDocs(id, d.value);
      if (vm && og.status === 'fulfilled') putOrigins(id, og.value);
      if (vm) { if (c.status === 'fulfilled') putCerts(id, c.value); else putCerts(id, [], true); }
    }
  };

  // ── actions (live เท่านั้น) ──
  const onFill = (d) => {
    if (mock) return;
    fillRef.current = d.req;
    fileInput.current?.click();
  };
  const uploadAndMatch = async (req, file) => {
    const oldDocId = req.matched_document_id;
    const doc = await db.uploadDocument({
      shipment: current.raw, consignmentId: req.consignment_id,
      reqType: req.req_type, file, actor,
    });
    await db.matchDocument(req, doc.id, actor, current.dbId);
    if (oldDocId && oldDocId !== doc.id) await db.deleteDocument(oldDocId); // แทนไฟล์เดิม ไม่ให้ค้าง
    refresh();
  };
  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !fillRef.current || !current) return;
    try { await uploadAndMatch(fillRef.current, file); }
    catch (er) { setActMsg(er.message ?? String(er)); }
  };
  // ลากไฟล์วางบนการ์ด → อัพ + จับคู่ช่องนั้นเลย (แทนของเดิมถ้ามี)
  const onDropFile = async (d, file) => {
    if (mock || !d.req || !current) return;
    try { await uploadAndMatch(d.req, file); }
    catch (er) { setActMsg(er.message ?? String(er)); }
  };
  // เอาไฟล์ออกจากการ์ด → กลับเป็นช่องว่าง (เผื่อใส่ผิด)
  const restorePermit = async (d) => {
    try { await db.setPermitWaived(d.req, false, { shipmentId: current.dbId, actor }); await refresh().catch(() => {}); }
    catch (er) { setActMsg(er.message ?? String(er)); }
  };
  const onRemoveMatched = async (d) => {
    if (mock || !d.docId) return;
    try { await db.deleteDocument(d.docId); refresh(); }
    catch (er) { setActMsg(er.message ?? String(er)); }
  };
  // แนบเอกสารเพิ่มเติม (Freight Invoice / อื่นๆ) — ระบุชนิดผ่าน ref ก่อนเปิด file picker
  const attachAs = (rt) => { trayReqType.current = rt; trayInput.current?.click(); };
  const onTrayChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const rt = trayReqType.current; trayReqType.current = null;
    if (!file || !current) return;
    try {
      await db.uploadDocument({
        shipment: current.raw, consignmentId: null,
        reqType: rt ?? guessReqType(file.name) ?? 'attachment', file, actor,
      });
      putDocs(current.dbId, await db.getDocuments(current.dbId));
    } catch (er) { setActMsg(er.message ?? String(er)); }
  };
  const suggestFor = (doc) => {
    const rt = doc.req_type ?? guessReqType(doc.file_name ?? '');
    if (!rt || !current) return null;
    return current.requirements.find(r =>
      r.req_type === rt && r.required && r.status === 'missing') ?? null;
  };
  const onSuggestMatch = async (doc) => {
    const req = suggestFor(doc);
    if (!req) return;
    try {
      await db.matchDocument(req, doc.id, actor, current.dbId);
      refresh();
    } catch (er) { setActMsg(er.message ?? String(er)); }
  };
  // จับคู่ไฟล์ใน tray กับช่องที่เลือกเอง (reqId) — กันไฟล์ค้าง
  const onAssignTray = async (docId, reqId) => {
    const req = current.requirements.find(r => r.id === reqId);
    if (!req) return;
    try { await db.matchDocument(req, docId, actor, current.dbId); refresh(); }
    catch (er) { setActMsg(er.message ?? String(er)); }
  };
  const onRemoveTray = async (docId) => {
    try { await db.deleteDocument(docId); refresh(); }
    catch (er) { setActMsg(er.message ?? String(er)); }
  };
  // เปลี่ยนวิธีขนส่ง (RPC จัดการช่อง B/L↔AWB) + จดลง journal
  const switchMode = async (to) => {
    if (!current || current.raw?.mode === to) return;
    await db.setTransportMode(current.dbId, to);
    await db.logEvent(current.dbId, null, 'mode_changed', null, actor, { from: current.raw?.mode, to });
  };
  const onOpenDoc = (d) => {
    if (!d.filePath) return;
    // เปิดแท็บทันทีตอนคลิก (user gesture) แล้วค่อยพา URL ที่ sign เสร็จเข้าไป
    // — กัน browser บล็อก popup เพราะ window.open ถูกเรียกหลัง await (network)
    const w = window.open('', '_blank');
    db.documentUrl(d.filePath)
      .then(url => { if (w) w.location.href = url; else window.open(url, '_blank'); })
      .catch(er => { if (w) w.close(); setActMsg(`เปิดไฟล์ไม่สำเร็จ: ${er.message ?? String(er)}`); });
  };
  const [packetBusy, setPacketBusy] = useState(false);
  const onPacket = async () => {
    if (!current) return;
    setPacketBusy(true); setActMsg(null);
    try { await db.buildPacket(current.raw); }
    catch (er) { setActMsg(er.message ?? String(er)); }
    setPacketBusy(false);
  };
  const onClose = async () => {
    if (!current) return;
    const id = current.id;
    try { await db.closeShipment(current.dbId, actor); if (selRef.current === id) setRailView('done'); await loadAll(selRef.current); }
    catch (er) { setActMsg(er.message ?? String(er)); }
  };
  const onReopen = async () => {
    if (!current) return;
    const id = current.id;
    try { await db.reopenShipment(current.dbId, actor); if (selRef.current === id) setRailView('active'); await loadAll(selRef.current); }
    catch (er) { setActMsg(er.message ?? String(er)); }
  };
  const onNudge = (card) => {
    if (mock || !current) return;
    const req = card.req;
    const consignment = current.consignments.find(c => c.id === req.consignment_id) ?? null;
    const draft = draftNudge({ requirement: req, shipment: current.raw, consignment });
    setModal({ nudge: { draft, requirement: req } });
  };
  const onNudgeSent = async (channel, text) => {
    if (!current || !modal?.nudge) return;
    await db.logEvent(current.dbId, modal.nudge.requirement.consignment_id ?? null,
      'nudge_sent', null, actor, { req_type: modal.nudge.requirement.req_type, channel });
    refresh();
  };
  const [flash, setFlash] = useState(null);
  const showFlash = (msg, ms = 3500) => { setFlash(msg); setTimeout(() => setFlash(null), ms); };
  const onSendBroker = async (gate) => {
    if (gate.blocked || !current) return;
    try {
      await db.logEvent(current.dbId, null, 'sent_to_broker', 'customs_submitted', actor, {});
      showFlash(gate.advisory
        ? `✓ ส่ง draft ให้ ${current.raw?.forwarder || 'broker'} แล้ว — บันทึกในประวัติการทำงาน`
        : `✓ ส่งชุดเอกสารให้ ${current.raw?.forwarder || 'broker'} แล้ว`);
      refresh();
    } catch (er) { setActMsg(er.message ?? String(er)); }
  };

  // ── render ──
  if (loading) return <div className="placeholder"><h2>กำลังโหลด…</h2></div>;
  if (pending) return <DbPending onRetry={() => loadAll(selRef.current)}/>;
  if (error) return (
    <div className="placeholder">
      <h2>โหลดข้อมูลไม่สำเร็จ</h2><p className="mono">{error}</p>
      <button className="tab" onClick={() => loadAll(selRef.current)}>ลองใหม่</button>
    </div>
  );
  if (!current) return (
    <div className="placeholder">
      <h2>ยังไม่มีตู้ในระบบ</h2>
      <p>① สร้างตู้ → ② เพิ่มของจาก supplier → ③ deck เอกสารจะคำนวณให้เอง แล้วลากไฟล์มาจับคู่</p>
      <button className="btn1" onClick={() => setModal('create')}>＋ สร้างตู้แรก</button>
      {modal === 'create' && (
        <CreateShipmentModal onClose={() => setModal(null)} suggestCode={suggestCode(ships.map(s => s.raw?.code || s.id))}
          onSubmit={async (f, { alreadyDone } = {}) => {
            const s = await db.createShipment(clean(f), actor);
            if (alreadyDone) { await db.closeShipment(s.id, actor); setRailView('done'); }
            await loadAll(s.code);
          }}/>
      )}
    </div>
  );

  const board = mock ? mockBoard(current) : toBoardVM(current, documents);
  // สีหัวกลุ่ม supplier: ใช้สีที่ตั้งไว้ในแท็บ "แบรนด์" ถ้ามี (ไม่งั้น hueFor สุ่มจากชื่อเหมือนเดิม)
  if (!mock && partners.length) {
    const hueMap = new Map();
    for (const p of partners) if (p.brand_hue) for (const n of db.partnerNames(p)) hueMap.set(db.normName(n), p.brand_hue);
    for (const g of board.groups) { const h = hueMap.get(db.normName(g.nm)); if (h) g.hue = h; }
  }
  const gate = mock ? { blocked: true, reason: 'MOCK' }
    : !certsReady ? { blocked: true, loading: true, reason: null }   // ใบ CO ของตู้นี้ยังโหลดไม่เสร็จ
    // โหลดใบ CO ไม่ขึ้น = ตัดสินด่านไม่ได้ → broker ส่ง draft ได้เหมือน "ยังไม่ยืนยัน" · พิธีการเองล็อกไว้ก่อน
    : certState.failed ? { blocked: current.raw?.clearance === 'self', advisory: true, failed: true,
        reason: 'โหลดข้อมูล Form/CO ของตู้นี้ไม่สำเร็จ — ยังบอกไม่ได้ว่า Form ผ่านหรือยัง' }
    : coGate(current, certs);
  const t = tally(board);
  const sentToBroker = ['customs_submitted', 'duty_paid', 'delivered', 'received'].includes(current.raw?.status);

  return (
    <div className="app">
      {flash && <div className="flash">{flash}</div>}
      <input ref={fileInput} type="file" style={{ display: 'none' }} onChange={onFileChosen}/>
      <input ref={trayInput} type="file" style={{ display: 'none' }} onChange={onTrayChosen}/>

      <div className="rail">
        <div className="rail-h">
          <span className="t">Pipeline</span>
          {!mock && <button className="railadd" onClick={() => setModal('create')}>＋ ตู้ใหม่</button>}
        </div>
        <>
            <div className="railfind">
              <svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>
              <input value={q} onChange={e => setQ(e.target.value)}
                placeholder="ค้นหาตู้ — รหัสตู้ · ชื่อแบรนด์ · เลข invoice · ชื่อไฟล์ · ท่า"/>
              {q && <button className="railfind-x" onClick={() => setQ('')} title="ล้างคำค้น">✕</button>}
            </div>
            {hits ? (
              <div className="railfind-r">
                พบ <b>{hits.length}</b> ตู้{hitDone > 0 && <> · ปิดงานแล้ว {hitDone}</>}
                {!hits.length && <> — ลองพิมพ์รหัสตู้ (เช่น NG-2609) หรือเลขบนชื่อไฟล์</>}
              </div>
            ) : mock ? null : (
              <div className="railtabs">
                <button className={`railtab ${railView === 'active' ? 'on' : ''}`} onClick={() => setRailView('active')}>
                  กำลังทำ ({activeShips.length})
                </button>
                <button className={`railtab ${railView === 'done' ? 'on' : ''}`} onClick={() => setRailView('done')}>
                  เสร็จแล้ว ({doneShips.length})
                </button>
              </div>
            )}
        </>
        <div className="rail-list">
          {(hits ?? (mock ? ships : railShips)).map(x => (
            <RailCard key={x.id} s={x} sel={x.id === current.id} onSelect={() => setCurrentId(x.id)}/>
          ))}
          {!(hits ?? (mock ? ships : railShips)).length && (
            <div className="jr-empty">
              {hits ? `ไม่พบตู้ที่ตรงกับ “${q}”` : railView === 'done' ? 'ยังไม่มีตู้ที่ปิดงาน' : 'ไม่มีตู้ที่กำลังทำ'}
            </div>
          )}
        </div>
      </div>

      <div className="main">
        {actMsg && (
          <div className="actbar">
            <span>ทำรายการไม่สำเร็จ: {actMsg}</span>
            <button className="actbar-x" onClick={() => setActMsg(null)} title="ปิด">✕</button>
          </div>
        )}
        <div className={`console u-${current.urgency}`}>
          <div className="c-bar"/>
          <div className="c-head">
            <div className="c-h1">
              <span className="id">{current.id}</span>
              <span className="bn">{current.displayName}</span>
              {mock
                ? <span className={`pill mode ${current.mode}`}><ModeIcon mode={current.mode}/>
                    {current.mode === 'air' ? 'AIR' : 'SEA'}<em>{current.mode === 'air' ? 'ทางอากาศ' : 'ทางเรือ'}</em></span>
                : <button className={`pill mode ${current.mode} modeswitch`} onClick={() => setModal({ switchMode: current.raw })}
                    title="กดเพื่อเปลี่ยนวิธีขนส่ง (ทางเรือ ↔ ทางอากาศ) — ระบบปรับช่อง B/L ↔ AWB ให้">
                    <ModeIcon mode={current.mode}/>{current.mode === 'air' ? 'AIR' : 'SEA'}
                    <em>{current.mode === 'air' ? 'ทางอากาศ' : 'ทางเรือ'}</em><span className="sw">✎</span></button>}
              <span className="pill">{current.route}</span>
              <span className="pill phase">{current.phase}</span>
              {current.done ? <Stamp/> : <span className="pill eta">{current.eta}</span>}
              {!mock && <button className="pill editpill" onClick={() => setModal({ editShip: current.raw })}>✎ แก้ข้อมูลตู้</button>}
              {!mock && <button className="pill editpill" disabled={packetBusy}
                onClick={onPacket} title="ดาวน์โหลดไฟล์เอกสารทั้งหมดของตู้เป็น ZIP">
                {packetBusy ? 'กำลังรวม…' : '⬇ ชุดเอกสาร'}</button>}
              {!mock && aiOn && !current.done && current.consignments.length > 0 && (
                <MaterialGapButton key={current.dbId} consignmentIds={current.consignments.map(c => c.id)}
                  // ตู้เดียวมีหลาย invoice ได้ — AI ใช้วันที่เลือกซีซั่น ใช้วันล่าสุด (ไม่ส่งถ้าไม่มี)
                  docDate={current.consignments.map(c => c.invoice_date).filter(Boolean).sort().at(-1) ?? null}
                  className="pill editpill" label="หาวัสดุที่ยังว่าง"
                  title="ให้ AI ช่วยค้นวัสดุของสินค้าในตู้นี้ที่ยังไม่มีวัสดุ — บอกจำนวนรุ่นและค่าใช้จ่ายก่อนเริ่ม ผลเป็นร่างจนกว่าคนจะกดยืนยัน"
                  onMessage={(m) => showFlash(m, 9000)} onOpenReview={onOpenMaterials}/>
              )}
              {!mock && !db.isDone(current.raw?.status) && (
                <button className={`pill donepill ${daysUntil(current.raw?.eta) <= 0 ? 'ready' : ''}`} onClick={onClose}
                  title="รับของเข้าคลังครบแล้ว → ย้ายไปแท็บ 'เสร็จแล้ว'">✓ ปิดงาน (รับของครบแล้ว)</button>
              )}
              {!mock && db.isDone(current.raw?.status) && (
                <button className="pill editpill" onClick={onReopen} title="เปิดงานกลับมาแก้ (เผื่อปิดผิด)">↺ เปิดงานกลับ</button>
              )}
            </div>
            <div className="c-meter">
              <div className="bar-wrap">
                <div className="bar-top">
                  <span>จับคู่เอกสาร <span className="n">{t.matched}/{t.req}</span></span>
                  <span className="n">{current.pct}%</span>
                </div>
                <Bar pct={current.pct}/>
              </div>
              <div className="tally">
                {t.flag > 0 && <span className="tchip f">! {t.flag} ตรวจ</span>}
                {t.miss > 0 && <span className="tchip m">○ {t.miss} รอ</span>}
              </div>
            </div>
            <div className="next">
              <span className="lb">ทำต่อ</span>
              <span className="tx">{current.next}</span>
              {!mock && current.raw?.clearance !== 'self' && (
                sentToBroker ? (
                  <button className="go sent" title="ส่งให้ broker แล้ว — กดเพื่อส่งซ้ำ (เช่นแก้เอกสาร)"
                    disabled={gate.loading} onClick={() => onSendBroker(gate)}>✓ ส่ง broker แล้ว · ส่งซ้ำ</button>
                ) : (
                  <button className={`go ${gate.advisory ? 'warn' : ''} ${gate.loading ? 'off' : ''}`}
                    title={gate.reason ?? 'บันทึก event ส่งชุดเอกสารให้ broker'}
                    disabled={gate.loading} onClick={() => onSendBroker(gate)}>
                    {gate.loading ? 'กำลังโหลด…' : gate.advisory ? 'ส่ง draft ให้ broker →' : 'ส่ง broker →'}
                  </button>
                )
              )}
              {!mock && current.raw?.clearance === 'self' && (
                <button className={`go ${gate.blocked ? 'off' : ''}`}
                  disabled={gate.blocked} title={gate.reason ?? 'บันทึกว่าเดินพิธีแล้ว'}
                  onClick={() => onSendBroker(gate)}>
                  {gate.loading ? 'กำลังโหลด…' : gate.failed ? '● โหลด Form ไม่สำเร็จ' : gate.blocked ? '● Form ยังไม่ผ่าน (ล็อก)' : 'ยื่นพิธีการ →'}
                </button>
              )}
              {mock && <button className="go">ทวง <span>→</span></button>}
            </div>
            {!mock && !current.done && gate.reason && gate.reason !== 'MOCK' && (
              <div className={`gatenote ${gate.advisory ? 'advisory' : ''}`}>
                {gate.reason}
                {gate.failed && <> <button className="mini" onClick={refresh}>ลองใหม่</button></>}
              </div>
            )}
          </div>
          {current.alert && <div className="alert"><ClockIcon/><span>{current.alert}</span></div>}
          <div className="board">
            {!mock && current.consignments.length === 0 && (
              <div className="firststep">
                <div className="fs-t">เริ่มตู้นี้ยังไง — 3 ขั้น</div>
                <div className="fs-steps">
                  <div className="fs-step"><b>①</b> เพิ่มของจาก supplier (ใครส่ง + ประเทศต้นทาง)</div>
                  <div className="fs-step"><b>②</b> ระบบคำนวณเอกสารที่ต้องใช้ให้เอง — invoice / ใบขน / CO / ใบอนุญาต ตามชนิดสินค้า</div>
                  <div className="fs-step"><b>③</b> ลากไฟล์มาจับคู่ทีละใบ เห็นเลยว่าครบหรือขาดอะไร</div>
                </div>
                <button className="btn1" onClick={() => setModal('consign')}>＋ เพิ่มของจาก supplier</button>
                <div className="fs-hint">ช่องด้านล่าง (Booking / ใบตรวจรับเข้าคลัง) คือเอกสารระดับตู้ที่ต้องใช้ทุกตู้</div>
              </div>
            )}

            <div className="catleg">
              <span className="cl money">◆ เงิน / Invoice</span>
              <span className="cl ship">◆ ขนส่ง</span>
              <span className="cl origin">◆ แหล่งกำเนิด / CO</span>
              <span className="cl permit">◆ ใบอนุญาต</span>
              <span className="cl customs">◆ ศุลกากร</span>
              <span className="cl wh">◆ คลัง</span>
            </div>

            {board.groups.map((g, i) => (
              <DocGroup key={g.consignmentId ?? i} g={g} onFill={onFill} onOpen={onOpenDoc}
                onNudge={onNudge} onRemove={onRemoveMatched} onDropFile={onDropFile} canNudge={!mock}
                onWaivePermit={!mock ? setWaiveDoc : undefined} onRestorePermit={!mock ? restorePermit : undefined}
                notice={!mock && g.consignmentId ? (() => {
                  const coReq = current.requirements.find(r =>
                    r.consignment_id === g.consignmentId && r.req_type === 'certificate_of_origin');
                  const cons = current.consignments.find(c => c.id === g.consignmentId);
                  // key = ช่อง CO ของ supplier ใบนี้ → สลับตู้แล้วขั้น "ยืนยัน" ที่กดค้างไว้ไม่ติดไปตู้อื่น
                  return coReq ? (
                    <FtaStrip key={coReq.id} req={coReq} cons={cons} counts={origins[g.consignmentId]}
                      onWaive={async (reason) => {
                        await db.setFtaWaived(coReq, true, { shipmentId: current.dbId, actor, reason });
                        await refresh().catch(() => {});   // บันทึกแล้ว — โหลดใหม่พลาดไม่ใช่ "บันทึกไม่สำเร็จ"
                      }}
                      onClaim={async () => {
                        await db.setFtaWaived(coReq, false, { shipmentId: current.dbId, actor });
                        await refresh().catch(() => {});
                      }}/>
                  ) : null;
                })() : null}
                actions={!mock && g.consignmentId ? (
                  <span className="grp-actions">
                    <button className="mini" onClick={() => setModal({
                      editCons: current.consignments.find(c => c.id === g.consignmentId) })}>✎ แก้</button>
                    <button className="mini go2" onClick={() => onOpenDossier?.(g.consignmentId)} title="ใส่ / จัดการรายการสินค้า แล้วพิมพ์ใบข้อมูลสินค้าในนั้น">＋ ใส่/พิมพ์ ใบข้อมูลสินค้า →</button>
                    {certsReady && !certState.failed && needsCOVerify(current, certs, g.consignmentId) && (
                      <button className="mini hot" onClick={() => setModal({
                        verify: current.consignments.find(c => c.id === g.consignmentId) })}>
                        ตรวจ Form
                      </button>
                    )}
                  </span>
                ) : null}/>
            ))}
            <DocGroup g={board.shipmentDocs} onFill={onFill} onOpen={onOpenDoc}
              onNudge={onNudge} onRemove={onRemoveMatched} onDropFile={onDropFile} canNudge={!mock}
                onWaivePermit={!mock ? setWaiveDoc : undefined} onRestorePermit={!mock ? restorePermit : undefined}/>

            {!mock && current.consignments.length > 0 && (
              <div className="grp-h" style={{ marginTop: 4 }}>
                <button className="mini" onClick={() => setModal('consign')}>＋ เพิ่มของจาก supplier อีกเจ้า (LCL)</button>
              </div>
            )}

            {!mock && (
              // key = ตู้ → สลับตู้แล้วบันทึกเริ่มใหม่ (เดิมโน้ต/ช่อง "ผูกกับ" ของตู้ก่อนค้าง → จดลงตู้ใหม่แต่ผูกของตู้เก่าได้)
              <Journal key={current.dbId} shipment={current} consignments={current.consignments}
                actor={actor} onLogged={refresh}/>
            )}

            <div className="tray">
              <div className="tray-h">
                <FileIcon/>เอกสารเพิ่มเติม (แนบไว้กับตู้)
                <span className="dz">
                  {mock ? 'ลากไฟล์มาวาง หรือแตะช่องว่างเพื่อเติม'
                    : <>
                        <button className="mini" onClick={() => attachAs('freight_invoice')}>＋ Freight Invoice</button>
                        <button className="mini" onClick={() => attachAs('attachment')}>＋ เอกสารอื่น</button>
                      </>}
                </span>
              </div>
              {board.tray.length ? board.tray.map(d => (
                <div className="tfile" key={d.id}>
                  <FileIcon/>
                  <span className="tn">
                    {d.req_type && reqLabel(d.req_type) !== d.req_type && <b className="tlab">{reqLabel(d.req_type)}</b>}
                    {d.file_name}
                  </span>
                  <span className="sugg">
                    {mock ? (<><span className="lab">แนะนำ →</span><button className="match">{d.suggestion}</button></>)
                      : (<>
                          {suggestFor(d) && (
                            <button className="match" onClick={() => onSuggestMatch(d)}>
                              จับคู่: {reqLabel(suggestFor(d).req_type)}
                            </button>
                          )}
                          <select className="trayassign" value=""
                            onChange={e => { if (e.target.value) onAssignTray(d.id, e.target.value); }}>
                            <option value="">จับคู่ช่อง (ถ้าใช่)…</option>
                            {current.requirements.filter(r => r.status === 'missing' || r.status === 'flagged')
                              .map(r => <option key={r.id} value={r.id}>{reqLabel(r.req_type)}</option>)}
                          </select>
                          <button className="trayx" title="ลบไฟล์นี้ทิ้ง" onClick={() => onRemoveTray(d.id)}>✕</button>
                        </>)}
                  </span>
                </div>
              )) : <div className="tray-empty">ยังไม่มีเอกสารเพิ่มเติม — กด ＋ แนบ Freight Invoice หรือเอกสารอื่นที่ไม่มีช่องเฉพาะ</div>}
            </div>
          </div>
        </div>
        <div className="note">
          {mock ? 'MOCK DATA · ยังไม่ต่อ Supabase' : 'INBOUNDpoint · สถานะจริงจาก schema inbound'}
          {!mock && current && <> · <button className="loglink" onClick={() => setModal({ log: current })}>ประวัติการทำงาน</button></>}
        </div>
      </div>

      {modal === 'create' && (
        <CreateShipmentModal onClose={() => setModal(null)} suggestCode={suggestCode(ships.map(s => s.raw?.code || s.id))}
          onSubmit={async (f, { alreadyDone } = {}) => {
            const s = await db.createShipment(clean(f), actor);
            if (alreadyDone) { await db.closeShipment(s.id, actor); setRailView('done'); }
            await loadAll(s.code);
          }}/>
      )}
      {waiveDoc && current && (
        <PermitWaiveModal d={waiveDoc} onClose={() => setWaiveDoc(null)}
          onSave={async (reason) => {
            await db.setPermitWaived(waiveDoc.req, true, { shipmentId: current.dbId, actor, reason });
            await refresh().catch(() => {});
          }}/>
      )}
      {modal === 'consign' && current && (
        <AddConsignmentModal onClose={() => setModal(null)} supplierNames={supplierNames}
          onSubmit={async f => { await db.addConsignment(current.dbId, f, actor); refresh(); }}/>
      )}
      {modal?.verify && (
        <VerifyCOModal consignment={modal.verify} onClose={() => setModal(null)}
          onSubmit={async f => {
            await db.verifyCO({ consignmentId: modal.verify.id, shipmentId: current.dbId,
              formType: f.formType, referenceNo: f.referenceNo, originCriteria: f.originCriteria,
              issuedDate: f.issuedDate, notes: f.notes, actor });
            refresh();
          }}/>
      )}
      {modal?.nudge && (
        <NudgeModal draft={modal.nudge.draft} requirement={modal.nudge.requirement}
          onClose={() => setModal(null)} onSent={onNudgeSent}/>
      )}
      {modal?.log && <EventLog shipment={modal.log} onClose={() => setModal(null)}/>}
      {modal?.switchMode && (
        <SwitchModeModal shipment={modal.switchMode} onClose={() => setModal(null)}
          onSubmit={async (to) => { await switchMode(to); await loadAll(current.raw?.code); showFlash(`เปลี่ยนวิธีขนส่งเป็น ${to === 'air' ? 'ทางอากาศ (AWB)' : 'ทางเรือ (B/L)'} แล้ว — ช่องเอกสารปรับให้แล้ว`); }}/>
      )}
      {modal?.editShip && (
        <EditShipmentModal shipment={modal.editShip}
          hasInsurance={current.requirements.some(r => r.req_type === 'insurance')}
          onClose={() => setModal(null)}
          onSubmit={async (fields, { modeChanged, insurance, insuranceChanged }) => {
            await db.updateShipment(current.dbId, fields, { recompute: false });
            if (modeChanged) await switchMode(fields.mode);   // สลับช่อง B/L↔AWB ให้ถูก (deploy-24)
            if (insuranceChanged) await db.setShipmentInsurance(current.dbId, insurance);
            await loadAll(fields.code);
          }}
          onDelete={async () => { await db.voidShipment(current.dbId); setCurrentId(null); await loadAll(); }}/>
      )}
      {modal?.editCons && (
        <EditConsignmentModal consignment={modal.editCons} onClose={() => setModal(null)} supplierNames={supplierNames}
          onSubmit={async (fields, { originChanged }) => {
            await db.updateConsignment(modal.editCons.id, fields, current.dbId, { recompute: originChanged, actor });
            refresh();
          }}
          onDelete={async () => { await db.deleteConsignment(modal.editCons.id); await loadAll(current.id); }}/>
      )}
    </div>
  );
}

function tally(board) {
  const all = [...board.groups.flatMap(g => g.docs), ...board.shipmentDocs.docs];
  const req = all.filter(d => d.s !== 'na');
  return {
    matched: req.filter(d => d.s === 'ok').length, req: req.length,
    flag: all.filter(d => d.s === 'f').length, miss: all.filter(d => d.s === 'm').length,
  };
}

// ชื่อประเทศภาษาไทย (รหัสที่ไม่อยู่ในรายการ = โชว์รหัส)
const ccName = (c) => COMMON_COUNTRIES.find(([k]) => k === c)?.[1] ?? c;

// แถบสิทธิ์ FTA ใต้หัว supplier — 29 ต.ค. 69
//   user: "ตู้ SP-261015 ถูกส่งมาจากญี่ปุ่น ของข้างในไม่ได้ผลิตในญี่ปุ่นทั้งหมด เลยไม่มีสิทธิ์ของ Form"
//   ระบบขอ Form ตาม "ประเทศที่ส่งของมา" แต่สิทธิ์ FTA ดูจาก "ประเทศที่ผลิต" ของแต่ละชิ้น
//   ⇒ ของในใบนี้มีชิ้นที่ผลิตประเทศอื่น = เตือน + ให้คนตัดสิน "ไม่ใช้สิทธิ์ FTA" (ไม่ตัดสินเอง — บางครั้งใช้สิทธิ์ได้บางส่วน)
//   การไม่ใช้สิทธิ์ = เสียอากรเต็มอัตรา → ฝั่งปลอดภัย ไม่มีความเสี่ยงถูกประเมินย้อนหลังแบบ Form ที่ใช้ไม่ได้
function FtaStrip({ req, cons, counts, onWaive, onClaim }) {
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const from = String(cons?.origin_country ?? '').trim().toUpperCase();
  const entries = Object.entries(counts ?? {}).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((s, [, n]) => s + n, 0);
  const unknown = counts?.['?'] ?? 0;
  const same = counts?.[from] ?? 0;
  const other = total - unknown - same;
  const made = entries.filter(([c]) => c !== '?').map(([c, n]) => `${ccName(c)} ${n}`).join(' · ')
    + (unknown ? `${entries.length > 1 ? ' · ' : ''}ยังไม่ระบุ ${unknown}` : '');
  const fta = String(req.conditional_reason ?? '').split(' → ')[0].trim() || 'FTA';
  const run = async (fn) => {
    setBusy(true); setMsg(null);
    try { await fn(); setAsk(false); } catch (e) { setMsg(`บันทึกไม่สำเร็จ: ${e.message ?? String(e)}`); }
    setBusy(false);
  };

  if (req.waived) return (
    <div className="ftastrip off">
      <div className="fs-txt">
        <b>ไม่ใช้สิทธิ์ FTA ของ supplier ใบนี้ — ไม่ต้องมี Form · เสียอากรอัตราปกติ</b>
        {total > 0 && <span className="fs-made">ของผลิตที่ {made}</span>}
      </div>
      <button className="mini" disabled={busy} onClick={() => run(onClaim)}
        title="ใช้เมื่อตรวจแล้วว่าใช้สิทธิ์ได้ — ระบบจะกลับมาขอ Form และขึ้นในงานยืนยัน Form">
        {busy ? 'กำลังบันทึก…' : 'กลับมาใช้สิทธิ์ FTA'}</button>
      {msg && <div className="fs-err">{msg}</div>}
    </div>
  );
  // ไม่ต้องมี Form อยู่แล้ว (ประเทศนั้นไม่มี FTA กับไทย) · ยังไม่มีรายการ · ของผลิตประเทศเดียวกับที่ส่งมาทั้งหมด = ไม่ต้องเตือน
  if (!req.required || !from || other <= 0) return null;
  // 🐛 รีวิว 29 ต.ค.: ห้ามฟันธง "ไม่มีชิ้นไหนผลิตใน…" ตอนยังมีของที่ไม่รู้ประเทศ — ใบใหม่เว้นประเทศว่างโดยตั้งใจ
  //   ของที่ไม่รู้อาจผลิตในประเทศนั้นและใช้สิทธิ์ได้ · ไม่ใช้สิทธิ์ผิด = เสียอากรเต็มโดยไม่จำเป็น
  const head = unknown > 0
    ? `มีของที่ไม่ได้ผลิตใน${ccName(from)} ${other} รายการ · ยังไม่รู้ประเทศผลิตอีก ${unknown} รายการ`
    : same === 0
      ? `ของในใบนี้ไม่มีชิ้นไหนผลิตใน${ccName(from)} (รู้ประเทศผลิตครบทั้ง ${total} รายการ)`
      : `ผลิตใน${ccName(from)} ${same} จาก ${total} รายการ — ใช้สิทธิ์ได้เฉพาะ ${same} รายการนี้`;
  const why = unknown > 0
    ? ' · ฝากช่วยเติมประเทศผลิตก่อนตัดสิน — ในใบข้อมูลสินค้ากด "ประเทศผลิตจาก packing list"'
    : same === 0 ? ' · ถ้าใบนี้ใช้สิทธิ์ไม่ได้ ขอให้ช่วยกดด้านขวา ระบบจะเลิกตาม Form ให้'
    : ' · ถ้าทั้งใบไม่ใช้สิทธิ์ ขอให้ช่วยกดด้านขวา';
  return (
    <div className="ftastrip warn">
      <div className="fs-txt">
        <b>{head}</b>
        <span className="fs-made">ของผลิตที่ {made} (ตามใบข้อมูลสินค้า)</span>
        <span className="fs-why">
          ส่งจาก{ccName(from)}ไม่ได้แปลว่าได้สิทธิ์ {fta} — Form ใช้ได้กับของที่ผลิตใน{ccName(from)}เท่านั้น{why}
        </span>
      </div>
      {!ask ? (
        <button className="mini" disabled={busy} onClick={() => setAsk(true)}>ไม่ใช้สิทธิ์ FTA ใบนี้</button>
      ) : (
        <span className="fs-ask">
          <span>ใบนี้ไม่ใช้สิทธิ์ FTA — ไม่ต้องตาม Form และเสียอากรอัตราปกติ?</span>
          <button className="mini hot" disabled={busy}
            onClick={() => run(() => onWaive(`ส่งจาก ${from} · ของผลิตที่ ${made}`))}>
            {busy ? 'กำลังบันทึก…' : 'ยืนยัน ไม่ใช้สิทธิ์'}</button>
          <button className="mini" disabled={busy} onClick={() => setAsk(false)}>ยกเลิก</button>
        </span>
      )}
      {msg && <div className="fs-err">{msg}</div>}
    </div>
  );
}

function needsCOVerify(vm, certs, consignmentId) {
  const r = vm.requirements.find(x =>
    x.consignment_id === consignmentId && x.req_type === 'certificate_of_origin' && x.required);
  if (!r) return false;
  const cert = certs.find(c => c.consignment_id === consignmentId);
  return !(cert?.verified);
}

// SHP-ปีเดือน-ลำดับ2หลัก เช่น SHP-2607-01 — ต่อลำดับจากตู้ที่มีในเดือนนั้น (เดือนนึงหลายตู้ได้)
function suggestCode(existing = []) {
  const d = new Date();
  const ym = `${String(d.getFullYear() % 100)}${String(d.getMonth() + 1).padStart(2, '0')}`;
  const prefix = `SHP-${ym}`;
  const re = new RegExp(`^${prefix}(?:-(\\d+))?$`);
  let max = 0;
  for (const c of existing) {
    const m = String(c ?? '').match(re);       // ตรงเดือนนี้ (มีเลขลำดับ หรือแบบเก่าไม่มี)
    if (m) max = Math.max(max, m[1] ? parseInt(m[1], 10) : 1);
  }
  return `${prefix}-${String(max + 1).padStart(2, '0')}`;
}

function clean(f) {
  const out = { ...f };
  for (const k of Object.keys(out)) if (out[k] === '') out[k] = null;
  return out;
}
